// The things you can do to a chart you're looking at: take the numbers
// (CSV), take the picture (PNG or one-page PDF), or make it bigger.
//
// Shared by the chat bubble header and the expanded dialog so the two can't
// drift; `compact` drops the labels for the ~300px widget bubble, where icons
// are all that fit.

import type { Chart as ChartJSInstance } from 'chart.js'

import { downloadCsv, downloadPdf, downloadPng } from './chartExport'
import type { ChartSpec } from './chartSpec'

export interface ChartActionsProps {
  spec: ChartSpec
  /** The live chart + its resolved surface colour, for the PNG snapshot.
   *  Absent until the canvas mounts, which is when PNG stays disabled. */
  chart?: { instance: ChartJSInstance; surface: string } | null
  onExpand?: () => void
  compact?: boolean
}

function DownloadIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  )
}

function ImageIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <circle cx="8.5" cy="8.5" r="1.5" />
      <polyline points="21 15 16 10 5 21" />
    </svg>
  )
}

function PdfIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  )
}

function ExpandIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="15 3 21 3 21 9" />
      <polyline points="9 21 3 21 3 15" />
      <line x1="21" y1="3" x2="14" y2="10" />
      <line x1="3" y1="21" x2="10" y2="14" />
    </svg>
  )
}

export function ChartActions({ spec, chart, onExpand, compact = false }: ChartActionsProps) {
  const named = spec.title ? `: ${spec.title}` : ''
  return (
    <div className="nw-nchart-actions">
      <button
        type="button"
        className="nw-nchart-act"
        onClick={() => downloadCsv(spec)}
        aria-label={`Download CSV${named}`}
        title="Download CSV"
      >
        <DownloadIcon />
        {!compact && <span>CSV</span>}
      </button>
      <button
        type="button"
        className="nw-nchart-act"
        onClick={() => { if (chart) void downloadPng(chart.instance, spec, chart.surface) }}
        disabled={!chart}
        aria-label={`Download PNG${named}`}
        title="Download PNG"
      >
        <ImageIcon />
        {!compact && <span>PNG</span>}
      </button>
      <button
        type="button"
        className="nw-nchart-act"
        onClick={() => { if (chart) downloadPdf(chart.instance, spec, chart.surface) }}
        disabled={!chart}
        aria-label={`Download PDF${named}`}
        title="Download PDF"
      >
        <PdfIcon />
        {!compact && <span>PDF</span>}
      </button>
      {onExpand && (
        <button
          type="button"
          className="nw-nchart-act"
          onClick={onExpand}
          aria-label={`Expand chart${named}`}
          title="Expand"
        >
          <ExpandIcon />
        </button>
      )}
    </div>
  )
}
